import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Clock, Calendar as CalIcon, Users, CheckCircle2, ArrowLeft, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";
import { api, formatApiError } from "@/lib/api";

const todayStr = () => new Date().toISOString().split("T")[0];

export default function BookingPage() {
  const { content } = useContent();
  const { t, L } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const lessons = content?.lessons || [];
  const STEPS = [t("book.step.lesson"), t("book.step.datetime"), t("book.step.participants"), t("book.step.details"), t("book.step.confirm")];

  const [step, setStep] = useState(0);
  const [lessonId, setLessonId] = useState(params.get("lesson") || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [participants, setParticipants] = useState(1);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const selectedLesson = useMemo(() => lessons.find((l) => l.id === lessonId), [lessons, lessonId]);

  useEffect(() => {
    if (params.get("lesson") && lessons.length && lessons.find((l) => l.id === params.get("lesson"))) {
      setLessonId(params.get("lesson"));
    }
  }, [params, lessons]);

  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    setTime("");
    api
      .get(`/availability?date=${date}&lesson_id=${lessonId}`)
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date, lessonId]);

  const canNext = () => {
    if (step === 0) return !!lessonId;
    if (step === 1) return !!date && !!time;
    if (step === 2) return participants >= 1;
    if (step === 3) return form.first_name && form.last_name && form.email && form.phone;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        lesson_id: lessonId,
        lesson_name: L(selectedLesson?.name),
        date,
        time,
        participants,
        ...form,
      });
      setConfirmation(data);
      setStep(4);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="min-h-screen bg-ocean-900 flex items-center justify-center p-5" data-testid="booking-confirmation">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-ocean-800 rounded-2xl border border-white/10 p-8 sm:p-10 max-w-md w-full text-center text-sand-100">
          <div className="w-16 h-16 rounded-full bg-[#25D366]/20 text-[#25D366] flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-9 h-9" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold uppercase mb-2">{t("book.confirmed.title")}</h1>
          <p className="text-sand-100/60 mb-6">{t("book.confirmed.sub")}</p>
          <div className="bg-ocean-900 rounded-xl p-5 text-left space-y-2 font-mono text-sm mb-6">
            <div className="flex justify-between"><span className="text-sand-100/50">Ref</span><span className="text-gold">{confirmation.reference}</span></div>
            <div className="flex justify-between"><span className="text-sand-100/50">{t("book.sum.lesson")}</span><span>{confirmation.lesson_name}</span></div>
            <div className="flex justify-between"><span className="text-sand-100/50">{t("book.sum.date")}</span><span>{confirmation.date}</span></div>
            <div className="flex justify-between"><span className="text-sand-100/50">{t("book.sum.time")}</span><span>{confirmation.time} ({confirmation.duration_minutes} min)</span></div>
            <div className="flex justify-between"><span className="text-sand-100/50">{t("book.sum.participants")}</span><span>{confirmation.participants}</span></div>
          </div>
          <button onClick={() => navigate("/")} data-testid="confirmation-home-button" className="w-full bg-coral hover:bg-coral/90 text-white font-heading font-bold py-3.5 rounded-full">
            {t("book.confirmed.home")}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-100" data-testid="booking-page">
      <header className="bg-ocean-900 py-4 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 flex items-center justify-between">
          <Link to="/"><Logo variant="light" className="h-10 w-auto" /></Link>
          <Link to="/" className="flex items-center gap-2 text-sand-100/80 hover:text-gold text-sm font-mono">
            <ArrowLeft className="w-4 h-4" /> {t("book.home")}
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold uppercase text-ocean mb-2">{t("book.title")}</h1>
        <p className="text-ocean/60 mb-8 flex items-center gap-2">
          <Clock className="w-4 h-4 text-coral" /> {t("book.durationNote")}
        </p>

        {/* stepper */}
        <div className="flex items-center justify-between mb-10">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1 flex items-center">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm shrink-0 transition-colors ${
                  i < step ? "bg-coral text-white" : i === step ? "bg-ocean text-sand-100" : "bg-ocean/10 text-ocean/40"
                }`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`hidden sm:block text-[0.6rem] font-mono uppercase mt-1.5 tracking-wider ${i === step ? "text-ocean" : "text-ocean/40"}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < step ? "bg-coral" : "bg-ocean/10"}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
            {step === 0 && (
              <div className="space-y-3" data-testid="step-lesson">
                {lessons.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setLessonId(l.id)}
                    data-testid={`booking-lesson-${l.id}`}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                      lessonId === l.id ? "border-coral bg-white" : "border-ocean/10 bg-white hover:border-ocean/30"
                    }`}
                  >
                    <img src={l.image} alt={L(l.name)} className="w-20 h-20 rounded-lg object-cover shrink-0" />
                    <div className="flex-1">
                      <div className="font-heading font-bold text-ocean">{L(l.name)}</div>
                      <div className="text-sm text-ocean/60 line-clamp-2">{L(l.description)}</div>
                      <div className="font-mono text-xs text-coral mt-1">{l.price > 0 ? `€${l.price}` : t("book.onRequest")} · 1h 20m</div>
                    </div>
                    {lessonId === l.id && <Check className="w-5 h-5 text-coral shrink-0" />}
                  </button>
                ))}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6" data-testid="step-datetime">
                <div>
                  <label className="flex items-center gap-2 font-heading font-bold text-ocean mb-2"><CalIcon className="w-4 h-4" /> {t("book.chooseDate")}</label>
                  <input
                    type="date"
                    min={todayStr()}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="booking-date-input"
                    className="w-full bg-white border border-ocean/15 rounded-lg px-4 py-3 text-ocean focus:outline-none focus:border-coral"
                  />
                </div>
                {date && (
                  <div>
                    <label className="flex items-center gap-2 font-heading font-bold text-ocean mb-3"><Clock className="w-4 h-4" /> {t("book.availableTimes")}</label>
                    {loadingSlots ? (
                      <p className="text-ocean/50 font-mono text-sm">{t("book.loading")}</p>
                    ) : slots.length === 0 ? (
                      <p className="text-ocean/50 text-sm">{t("book.noSlots")}</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {slots.map((s) => (
                          <button
                            key={s.time}
                            disabled={!s.available}
                            onClick={() => setTime(s.time)}
                            data-testid={`booking-slot-${s.time}`}
                            className={`py-3 rounded-lg font-mono text-sm border-2 transition-all ${
                              !s.available
                                ? "border-ocean/5 bg-ocean/5 text-ocean/30 line-through cursor-not-allowed"
                                : time === s.time
                                ? "border-coral bg-coral text-white"
                                : "border-ocean/15 bg-white text-ocean hover:border-coral"
                            }`}
                          >
                            {s.time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-participants">
                <label className="flex items-center gap-2 font-heading font-bold text-ocean mb-4"><Users className="w-4 h-4" /> {t("book.participants")}</label>
                <div className="flex items-center justify-center gap-6 bg-white rounded-xl border border-ocean/10 py-8">
                  <button onClick={() => setParticipants((p) => Math.max(1, p - 1))} data-testid="participants-minus" className="w-12 h-12 rounded-full bg-ocean/10 text-ocean flex items-center justify-center hover:bg-ocean/20">
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="font-heading text-5xl font-extrabold text-ocean w-16 text-center" data-testid="participants-count">{participants}</span>
                  <button onClick={() => setParticipants((p) => Math.min(10, p + 1))} data-testid="participants-plus" className="w-12 h-12 rounded-full bg-ocean/10 text-ocean flex items-center justify-center hover:bg-ocean/20">
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4" data-testid="step-details">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input placeholder={t("book.firstName")} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="booking-firstname" className="bg-white border border-ocean/15 rounded-lg px-4 py-3 focus:outline-none focus:border-coral" />
                  <input placeholder={t("book.lastName")} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="booking-lastname" className="bg-white border border-ocean/15 rounded-lg px-4 py-3 focus:outline-none focus:border-coral" />
                </div>
                <input type="email" placeholder={t("book.sum.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" className="w-full bg-white border border-ocean/15 rounded-lg px-4 py-3 focus:outline-none focus:border-coral" />
                <input type="tel" placeholder={t("book.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" className="w-full bg-white border border-ocean/15 rounded-lg px-4 py-3 focus:outline-none focus:border-coral" />
                <textarea placeholder={t("book.notes")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-notes" rows={3} className="w-full bg-white border border-ocean/15 rounded-lg px-4 py-3 focus:outline-none focus:border-coral resize-none" />
              </div>
            )}

            {step === 4 && !confirmation && (
              <div className="bg-white rounded-xl border border-ocean/10 p-6 space-y-3" data-testid="step-summary">
                <h3 className="font-heading font-bold text-ocean text-lg mb-2">{t("book.summary")}</h3>
                {[
                  [t("book.sum.lesson"), L(selectedLesson?.name)],
                  [t("book.sum.date"), date],
                  [t("book.sum.time"), `${time} · 1h 20m`],
                  [t("book.sum.participants"), participants],
                  [t("book.sum.name"), `${form.first_name} ${form.last_name}`],
                  [t("book.sum.email"), form.email],
                  [t("book.sum.phone"), form.phone],
                  ...(form.notes ? [[t("book.sum.notes"), form.notes]] : []),
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-ocean/5 pb-2 text-sm">
                    <span className="text-ocean/50 font-mono">{k}</span>
                    <span className="text-ocean font-medium text-right">{v}</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* nav buttons */}
        <div className="flex gap-3 mt-8">
          {step > 0 && (
            <button onClick={() => setStep((s) => s - 1)} data-testid="booking-back" className="inline-flex items-center gap-2 border border-ocean/20 text-ocean font-heading font-semibold px-6 py-3.5 rounded-full hover:bg-white transition-colors">
              <ChevronLeft className="w-4 h-4" /> {t("book.back")}
            </button>
          )}
          {step < 4 && (
            <button
              onClick={() => canNext() && setStep((s) => s + 1)}
              disabled={!canNext()}
              data-testid="booking-next"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-ocean hover:bg-ocean-700 text-sand-100 font-heading font-bold px-6 py-3.5 rounded-full transition-all disabled:opacity-40"
            >
              {t("book.continue")} <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {step === 4 && (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="booking-confirm"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-coral hover:bg-coral/90 text-white font-heading font-bold px-6 py-3.5 rounded-full transition-all disabled:opacity-60"
            >
              {submitting ? t("book.sending") : t("book.confirm")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
